import Controller from '@ember/controller';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import {
  AddFolderCommand,
  MoveSongCommand,
  SortFoldersCommand,
  SortFoldersSongsAlphabeticallyCommand,
} from 'editor/models/folder-commands';
import { AddNewSongCommand } from 'editor/models/song-commands';
import type ProjectManagerService from 'editor/services/project-manager';
import { SongFile, SongFolder } from 'editor/services/project-manager';
import type { DrumsetFile } from 'editor/services/project-manager';
import type UndoManagerService from 'editor/services/undo-manager';

export default class RestrictedSongsController extends Controller {
  @service declare router: RouterService;
  @service declare projectManager: ProjectManagerService;
  @service declare undoManager: UndoManagerService;
  declare model: SongFolder[];

  @tracked filterText = '';

  get folder() {
    return this.projectManager.currentFolder;
  }

  get song() {
    return this.projectManager.currentSong;
  }

  get folders() {
    return this.projectManager.songFolders;
  }

  get sortMethod() {
    return this.projectManager.sortMethod;
  }

  @action
  async updateFilter(event: Event) {
    this.filterText = (event.target as HTMLInputElement).value;
  }

  get filter() {
    if (this.filterText.length >= 2) {
      return this.filterText;
    } else {
      return '';
    }
  }

  @action
  clearFilter() {
    this.filterText = '';
  }

  @action
  selectSong(folder: SongFolder, song: SongFile) {
    this.router.transitionTo(
      'restricted.songs.folder.song',
      folder.path,
      song.path,
    );
  }

  @action
  sortFolders(oldIndex: number, newIndex: number) {
    this.undoManager.executeCommand(
      new SortFoldersCommand(this.projectManager, oldIndex, newIndex),
    );
  }

  @action
  isSelected(folder: SongFolder) {
    return this.folder == folder;
  }

  @action
  async addFolder() {
    const folder = await this.undoManager.executeCommand(
      new AddFolderCommand(this.projectManager),
    );
    this.router.transitionTo('restricted.songs.folder', folder.path);
  }

  @action
  async addSong(folder?: SongFolder) {
    folder = folder ?? this.folder;
    if (!folder) {
      return;
    }
    const command = new AddNewSongCommand(this.projectManager, folder);
    command.on('execute', (song) => {
      this.router.transitionTo('restricted.songs.folder.song', song.path);
    });
    await this.undoManager.executeCommand(command);
  }

  @action
  async importSong(folder?: SongFolder) {
    folder = folder ?? this.folder;
    if (!folder) {
      return;
    }
    const commands = await this.projectManager.importSong(folder);
    if (commands) {
      commands.on('execute', async (results) => {
        const [song] = results;
        if (song instanceof SongFile) {
          this.router.transitionTo(
            'restricted.songs.folder.song',
            song.folder.path,
            song.path,
          );
        }
      });
      commands.on('reverse', () => {
        this.projectManager.currentFolder = undefined;
        this.router.transitionTo('restricted.songs.index');
      });

      await this.undoManager.executeCommand(commands);
    }
  }

  @action
  moveSong(
    fromElement: HTMLElement,
    fromIndex: number,
    toElement: HTMLElement,
    toIndex: number,
  ) {
    // eslint-disable-next-line ember/no-runloop
    next(() => {
      const folderFromIndex = Number(fromElement.dataset['folderIndex']);
      const folderToIndex = Number(toElement.dataset['folderIndex']);

      const fromFolder = this.folders[folderFromIndex];
      const toFolder = this.folders[folderToIndex];

      if (!fromFolder || !toFolder) {
        return;
      }

      const filter = this.filter?.trim().toLowerCase() ?? '';

      const getVisible = (folder: SongFolder): (SongFile | DrumsetFile)[] => {
        if (!filter) return folder.songs;

        if (folder.name.toLowerCase().includes(filter)) {
          return folder.songs;
        }

        return folder.songs.filter((song) =>
          song.name.toLowerCase().includes(filter),
        );
      };

      const visibleFrom = getVisible(fromFolder);
      const visibleTo = getVisible(toFolder);

      const movedSong = visibleFrom[fromIndex];
      if (!movedSong) {
        return;
      }

      const realFromIndex = fromFolder.songs.indexOf(movedSong);
      if (realFromIndex === -1) {
        return;
      }

      let realToIndex: number;

      if (toIndex >= visibleTo.length) {
        // Dragged to after the last visible song,
        // which is not necessarily the last song in the folder
        // don't drop last, drop after the last visible
        if (visibleTo.length === 0) {
          // No visible songs in target (aka, empty folder whose title match)
          // then insert at start
          // not probably technically necessary, it works without this chunk
          // but it's safer if things change in the future
          realToIndex = 0;
        } else {
          const lastVisible = visibleTo[visibleTo.length - 1];
          const lastVisibleIndex = toFolder.songs.indexOf(lastVisible);
          realToIndex = lastVisibleIndex + 1;
        }
      } else {
        // Dropped before visibleTo[toIndex]
        const beforeSong = visibleTo[toIndex];
        const beforeIndex = toFolder.songs.indexOf(beforeSong);

        if (beforeIndex === -1) {
          // Fallback: put it after the last visible song (or at end)
          if (visibleTo.length > 0) {
            const lastVisible = visibleTo[visibleTo.length - 1];
            realToIndex = toFolder.songs.indexOf(lastVisible) + 1;
          } else {
            realToIndex = toFolder.songs.length;
          }
        } else {
          realToIndex = beforeIndex;
        }
      }

      this.undoManager.executeCommand(
        new MoveSongCommand(fromFolder, realFromIndex, toFolder, realToIndex),
      );
    });
  }

  @action
  sortFoldersSongsAlphabetically() {
    switch (this.sortMethod) {
      case 'none':
        this.projectManager.sortMethod = 'AtoZ';
        break;
      case 'AtoZ':
        this.projectManager.sortMethod = 'ZtoA';
        break;
      case 'ZtoA':
        this.projectManager.sortMethod = 'AtoZ';
        break;
      default:
        this.projectManager.sortMethod = 'none';
        break;
    }
    const command = new SortFoldersSongsAlphabeticallyCommand(
      this.projectManager,
    );
    const song = this.projectManager.currentSong;
    command.on('execute', () => {
      if (song) {
        this.router.transitionTo(
          'restricted.songs.folder.song',
          song.folder.path,
          song.path,
        );
      }
    });
    this.undoManager.executeCommand(command);
  }
}
